/**
 * DECOR PRICING MODEL — decor is purely COSMETIC (no power/progression), so it isn't level-gated; its PRICE is
 * the only pacing. The goal is a satisfying SPREAD: cheap accents a new player can grab within minutes, mid-tier
 * pieces worth a short save-up, grand centrepieces that are a real goal, and a few legitimately EXTREVAGANT
 * showpieces that even a wealthy veteran feels as a purchase. A rich returner buying lots of decor at once is
 * fine — cosmetics are a healthy coin sink — so we price for the normal player's pace, not against the whale.
 *
 * This module owns the DESIRABILITY TIERS + the "time to afford" math the Dev "Decor Pricing" tool displays, so
 * prices can be reasoned about (and new items slotted in) by how long they take the average player to save for.
 */

/** Organizational DEV TAGS — a purely cosmetic label to GROUP/SORT decor in the Dev pricing tool. These are
 *  DEV-ONLY, never shown to players, and have NO effect on price or any game math. Each item's price is its own
 *  authored number (the static def price, or a `decorPrices` override); the tag is just a bucket for the admin. */
export type DecorTier = "accent" | "small" | "standard" | "feature" | "centerpiece" | "showpiece" | "grand";

export interface DecorTierDef {
  id: DecorTier;
  label: string;
  blurb: string;   // a note for the admin — what this bucket tends to hold. NOT a price.
}

/** The dev tag buckets, roughly ordered cheap → grand (a hint only — nothing enforces a price band). */
export const DECOR_TIERS: DecorTierDef[] = [
  { id: "accent",     label: "Accent",     blurb: "Pocket-change touches — grab a few in your first session." },
  { id: "small",      label: "Small",      blurb: "A quick save-up; the first things you buy on purpose." },
  { id: "standard",   label: "Standard",   blurb: "The everyday decor bucket." },
  { id: "feature",    label: "Feature",    blurb: "A deliberate feature piece worth working toward." },
  { id: "centerpiece",label: "Centerpiece",blurb: "A garden centrepiece — a mid-game goal." },
  { id: "showpiece",  label: "Showpiece",  blurb: "A statement showpiece; a serious save." },
  { id: "grand",      label: "Grand",      blurb: "A grand extravagance — the crown of a mature garden." },
];

/**
 * Per-item DEV TAG — a label for grouping decor in the Dev pricing tool ONLY. It does NOT set the price (each
 * item's price is authored on its def / in the `decorPrices` override). Re-tag freely; a missing tag is fine
 * (the item just shows untagged). Adjust an item's PRICE in the Dev tool or its def — never via this map.
 */
export const DECOR_TIER: Record<string, DecorTier> = {
  pebbles: "accent", pinwheel: "accent", rock: "accent", signpost: "accent", windChime: "accent", wateringCan: "accent",
  mossyRock: "small", flatBoulder: "small", torch: "small", stump: "small", rainBarrel: "small", woodpile: "small", barrel: "small", scarecrow: "small",
  rockCairn: "standard", lantern: "standard", planterBox: "standard", windowBox: "standard", wheelbarrow: "standard", birdhouse: "standard", fairyLights: "standard", hangingBasket: "standard", topiaryBall: "standard",
  boulder: "feature", log: "feature", birdBath: "feature", stoneUrn: "feature", sundial: "feature", gardenBench: "feature", topiarySpiral: "feature", hammock: "feature", obelisk: "feature",
  armillary: "centerpiece", wishingWell: "centerpiece", gardenArch: "centerpiece", poplarTree: "centerpiece", pineTree: "centerpiece", birchTree: "centerpiece",
  fountain: "showpiece", oakTree: "showpiece", mapleTree: "showpiece",
  cherryTree: "grand",
  // REWARD decor — earned from an achievement first, then sold in the shop at these tiers
  carvedOrb: "standard", windSpinner: "standard", twinLampPost: "feature", stoneLantern: "feature",
  brazierFrost: "feature", brazierGilt: "feature", brazierEmber: "feature", brazierAmethyst: "feature",
  brazierSpectral: "centerpiece", crystalObelisk: "showpiece",
};

// ── LIVE PRICE OVERRIDES (the `decorPrices` tuning key) — set by the Dev "Decor Pricing" tool. A per-id coin
//    price that wins over the static def price, so an admin can retune decor prices live. ──
export type DecorPriceOverrides = Record<string, number>;
let PRICE_OVERRIDES: DecorPriceOverrides = {};
export function setDecorPriceOverrides(m: DecorPriceOverrides | undefined): void {
  PRICE_OVERRIDES = (m && typeof m === "object") ? m : {};
}
export function getDecorPriceOverrides(): DecorPriceOverrides { return PRICE_OVERRIDES; }

/**
 * The EFFECTIVE coin price for a decor id: a live `decorPrices` override → the `fallback` (the item's authored
 * def price). Every shop/buy path should read THIS, not the raw def, so live tuning-tool edits take effect. The
 * dev TAG (DECOR_TIER) does NOT influence this — price is purely the per-item authored number. `fallback` lets
 * callers pass `decorDef(id).price` without this module importing decor.ts.
 */
export function effectiveDecorPrice(id: string, fallback: number): number {
  const o = PRICE_OVERRIDES[id];
  if (typeof o === "number" && isFinite(o)) return Math.max(0, Math.round(o));
  return fallback;
}

/**
 * REFERENCE EARNING RATES (coins/hour) for the "time to afford" readout — drawn from the fishing/gardening
 * progression sim (`[[fish-value-model]]` balance pass). Decor is bought in the garden scene but funded by any
 * activity under the single-coin economy, so these span a player's journey from a fresh start to fully maxed.
 * The tool shows time-to-afford at each, so a price can be read as "≈X min for a new player, ≈Y for a veteran".
 */
export interface EarningRef { id: string; label: string; coinsPerHour: number; }
export const EARNING_REFS: EarningRef[] = [
  { id: "new",     label: "New player",   coinsPerHour: 8000 },   // one activity, low level (≈ fishing L1 / 1-rack garden)
  { id: "mid",     label: "Mid-game",     coinsPerHour: 18000 },  // a few levels in, some gear (≈ fishing L16–22 / ~2 racks)
  { id: "veteran", label: "Veteran",      coinsPerHour: 39000 },  // fully geared / maxed (≈ fishing L50 / 5-rack garden)
];

/** Minutes for a player at `coinsPerHour` to save `price` from scratch (ignores existing balance). */
export function minutesToAfford(price: number, coinsPerHour: number): number {
  if (coinsPerHour <= 0) return Infinity;
  return (price / coinsPerHour) * 60;
}

/** A compact human string for a duration in minutes ("6 min", "1.4 h", "instant"). */
export function fmtAfford(minutes: number): string {
  if (!isFinite(minutes)) return "∞";
  if (minutes < 1) return "<1 min";
  if (minutes < 90) return `${Math.round(minutes)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}
